import {KazBlurImageComponent} from '../../../components/kaz-blur-image/kaz-blur-image.component';
import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import {Ad, AdType, CancelledPublication, Category, CreateAd, HolidayShift, PriceType, SubCategory} from "../../../interfaces/ad";
import {RFC3339} from "../../../interfaces/datetime";
import {ApiService} from "../../../services/api.service";
import {Base64} from "../../../interfaces/utils";
import {AuthenticationService} from "../../../services/auth.service";
import {Cipher, CipherType} from "../../../interfaces/cipher";
import {Router} from "@angular/router";
import {User} from "../../../interfaces/user";
import {
  CIPHER_FEE,
  getLocalityList,
  getSubCategoryList,
  PRICE_PER_PUBLICATION
} from '../../../utils/globals';
import {BillingAddress, emptyBillingAddress} from "../../../interfaces/address";
import {isValidIBANNumber} from "../../../utils/iban";
import {DateUtils} from "../../../utils/date";
import {clearPreviousAd, getPreviousAd} from "../../../utils/sessionStorage";
import {NgxImageCompressService} from "ngx-image-compress";
import { DefaultFrameComponent } from '../../../frames/default-frame/default-frame.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { KazInputComponent } from '../../../components/kaz-input/kaz-input.component';
import { KazButtonComponent } from '../../../components/kaz-button/kaz-button.component';
import { KazAdInfoComponent } from '../../../components/kaz-ad-info/kaz-ad-info.component';
import { CardCategoryHintComponent } from '../../../components/card-category-hint/card-category-hint.component';
import { CurrencyPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'kaz-create-ad',
    templateUrl: './create-ad.component.html',
    styleUrls: ['./create-ad.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [KazBlurImageComponent, DefaultFrameComponent, FaIconComponent, FormsModule, KazInputComponent, KazButtonComponent, KazAdInfoComponent, CardCategoryHintComponent, CurrencyPipe, DatePipe]
})
export class CreateAdComponent implements OnInit {
  private _api = inject(ApiService);
  private _compress = inject(NgxImageCompressService);
  private _auth = inject(AuthenticationService);
  private _router = inject(Router);

  @ViewChild('imageInput', {static: false}) imageInput!: ElementRef;

  showCipher: boolean = false;
  showAdInfo: boolean = false;

  showAdPreview: boolean = false;

  allAvailableDates: { publicationDate: Date, expirationDate: Date }[] = [];
  imageIndex: number = 0;
  titleImageIndex: number = 0;
  images: string[] = [];

  imageData: Base64[] = [];
  imageContentType: string[] = [];

  adType: AdType = AdType.OFFER;
  title: string = '';
  description: string = '';
  cipherType: CipherType = CipherType.NONE;
  priceType: PriceType = PriceType.FP;
  price: string = '0';
  publicationDates: RFC3339[] = [];

  ad: Partial<CreateAd> | null = null;

  isLoading: boolean = false;

  placementFee: number = 0;
  cipherFee: number = 0;

  categoryName: string = '';
  subCategoryName: string = '';
  localityName: string | null = null;

  categoryHint: 'CAR' | 'PROPERTY' | 'JOB' | 'NONE' = 'NONE';

  subCategoryList: SubCategory[] = getSubCategoryList();
  localityList: string[] = getLocalityList();

  showCategoryHint: boolean = false;

  firstnameCipher: string = '';
  lastnameCipher: string = '';
  streetCipher: string = '';
  houseNumberCipher: string = '';
  cityCipher: string = '';
  zipCipher: string = '';

  billingAddress: BillingAddress = emptyBillingAddress();

  cipher: Partial<Cipher> | null = null;
  ownUser: User | null = null;

  user: Partial<User> | null = null;

  publicationDateAmount: number = 4;

  isWrongImageTag: boolean = false;
  isBigImageFile: boolean = false;
  previousAdId: string | null = null;

  transferOldImages: boolean = true;

  holidayShifts: HolidayShift[] = [];
  cancelledPublications: CancelledPublication[] = [];

  async ngOnInit() {
    this.getPreviousAd();
    await this.fetchHolidayShifts();
    await this.fetchCancelledPublications();
    this.allAvailableDates = this.calculatePublicationDates();
    await this.getOwnUser();
  }

  getWeekDayName(date: Date): string {
    return DateUtils.getWeekday(date, false);
  }

  private calculateExpireDate(date: Date): Date {
    if (date.getDay() === 3) {
      const expDate = new Date(date);
      expDate.setDate(expDate.getDate() - 1);
      expDate.setHours(10, 0, 0, 0)
      return expDate
    } else {
      const expDate = new Date(date);
      expDate.setDate(expDate.getDate() - 2);
      expDate.setHours(15, 0, 0, 0)
      return expDate
    }
  }

  private calculatePublicationDates(): { publicationDate: Date, expirationDate: Date }[] {
    const today = DateUtils.today();
    const now = DateUtils.now();
    const weekdaysToAdd = [6, 3]; // 6 for Saturday, 3 for Wednesday

    let publicationDates: { publicationDate: Date, expirationDate: Date }[] = [];

    const dateIter = new Date(today);
    while (publicationDates.length < this.publicationDateAmount) {

      let d0 = new Date(dateIter);
      let d1 = this.calculateExpireDate(new Date(dateIter));

      for (const shift of this.holidayShifts) {
        if (DateUtils.isSameDay(d0, shift.appearanceDateOld) && DateUtils.isSameDay(d1, shift.deadlineOld)) {
          d0 = shift.appearanceDateNew;
          d1 = shift.deadlineNew;
        }
      }
      const isCancelled = this.cancelledPublications.some(c => DateUtils.isSameDay(c.date, d0));
      if (weekdaysToAdd.includes(dateIter.getDay()) && d1.getTime() > now.getTime() && !isCancelled) {
        publicationDates.push({publicationDate: d0, expirationDate: d1});
      }

      dateIter.setDate(dateIter.getDate() + 1); // Move to the next day
    }

    return publicationDates;
  }

  async createAd() {
    try {
      this.isLoading = true;
      if (this.price === null || this.price == "")
        this.price = "0";

      this.images.forEach(base64String => {
        const parts = base64String.split(',');

        if (parts.length === 2) {
          const header = parts[0];
          const base64 = parts[1];

          const contentType = header.split(':')[1].split(';')[0];

          this.imageContentType.push(contentType);
          this.imageData.push(base64);
        }
      });

      const subCategory = this.subCategoryList.find(sub => sub.name === this.subCategoryName);

      if (subCategory) {
        switch (this.cipherType) {
          case CipherType.CHAT:
          case CipherType.NONE:
            this.cipher = {
              type: this.cipherType,
            };
            break;
          case CipherType.LETTER:
            this.cipher = {
              type: this.cipherType,
              firstname: this.firstnameCipher,
              lastname: this.lastnameCipher,
              address: {
                street: this.streetCipher,
                number: this.houseNumberCipher,
                zip: this.zipCipher,
                city: this.cityCipher,
              }
            };
        }
        this.ad = {
          transferOldImages: this.transferOldImages,
          imageData: this.imageData,
          imageContentType: this.imageContentType,
          titleImageIndex: this.titleImageIndex,
          category: {
            name: subCategory.masterCategory,
            subName: subCategory.name,
          } as Category,
          adType: this.adType,
          title: this.title,
          description: this.description,
          cipher: this.cipher,
          priceType: this.priceType,
          price: +this.price,
          publicationDate: this.publicationDates,
          billingAddress: this.billingAddress,
        }

        if (this.ad.category && this.localityName && this.localityName !== '') this.ad.category.localityName = this.localityName;

        if (this.ownUser) this.ad.authorId = this.ownUser.id;

        if (this.previousAdId !== null) {
          this.ad.previousAdId = this.previousAdId;
        }
        //Create ad
        const data = await this._api.createAd(this.ad);

        // TODO-1 wieder einfügen machen
        // if (data.adType !== AdType.GOOD_DEED || subCategoryName === 'Entlaufen/Entflogen' ||  subCategoryName === 'Verloren') {
        //   window.location.href = await this._api.createAdCheckoutSession(data.id, data.authorId);
        // } else {
        //   this._router.navigate(['profile']).then();
        // }

        this._router.navigate(['profile']).then(); //TODO-1 wieder entfernen machen


        this.isLoading = false;
      }
    } catch (err) {
      console.log(err);
    } finally {
      this.isLoading = false;
    }
  }

  onAdTypeChange(typ: AdType) {
    this.adType = typ;
    this.price = "";
    this.publicationDates = [];
    this.cipherType = CipherType.NONE;
    this.placementFee = 0;
    this.cipherFee = 0;
    this.categoryName = this.getCategoryList().length === 1 ? this.getCategoryList()[0] : '';
    this.subCategoryName = '';
    this.localityName = null;
  }

  onAdPriceTypeChange(typ: PriceType) {
    this.priceType = typ;
  }

  onAdCipherTypeChange(typ: CipherType.CHAT | CipherType.LETTER) {
    if (this.cipherType !== typ)
      this.cipherType = typ;
    else
      this.cipherType = CipherType.NONE;


    switch (this.cipherType) {
      case CipherType.LETTER:
        this.cipherFee = CIPHER_FEE;
        break;
      case CipherType.CHAT:
      case CipherType.NONE:
      default:
        this.cipherFee = 0;
    }

    if (this.adType === AdType.GOOD_DEED) {
      this.cipherFee = 0;
    }

  }

  toggleDate(date: RFC3339) {
    if (this.publicationDates.includes(date)) {
      this.publicationDates = this.publicationDates.filter(d => d !== date);
      if (this.adType !== AdType.GOOD_DEED || this.subCategoryName === 'Entlaufen/Entflogen' || this.subCategoryName === 'Verloren')
        this.placementFee -= PRICE_PER_PUBLICATION;
    } else {
      this.publicationDates.push(date);
      if (this.adType !== AdType.GOOD_DEED || this.subCategoryName === 'Entlaufen/Entflogen' || this.subCategoryName === 'Verloren')
        this.placementFee += PRICE_PER_PUBLICATION;
    }
  }

  onSelectImage() {
    this.isWrongImageTag = false;
    this.isBigImageFile = false;
    this.imageInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const fileSizeMB = file.size / (1024 * 1024); // Convert bytes to MB
      if (fileSizeMB > 12) {
        this.isBigImageFile = true;
        return;
      }

      const reader = new FileReader();
      let image: string = '';

      reader.onload = (e) => {
        image = e.target?.result as string;
        if (image !== '' && input.files !== null && this.endsWithImageTag(input.files[0])) {
          this._compress.compressFile(image, -1, 100, 90, 1024, 1024).then(img => {
            this.images.push(img);
          }).catch(err => {
            console.error('Komprimierung fehlgeschlagen: ', err);
          });
        } else {
          this.isWrongImageTag = true;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  private endsWithImageTag(file: File) {
    return file.name.toLowerCase().endsWith('.png') || file.name.toLowerCase().endsWith('.jpeg') || file.name.toLowerCase().endsWith('.jpg')
  }

  deleteImage(image: string) {
    const index = this.images.indexOf(image);
    if (index !== -1) {
      //entferne Bild
      this.images.splice(index, 1);

      if (index === this.titleImageIndex) {
        this.titleImageIndex = 0;
        this.imageIndex = this.titleImageIndex
      }
      if (index < this.titleImageIndex)
        this.titleImageIndex -= 1;
    }
  }

  setAsTitleImage(image: string) {
    const index = this.images.indexOf(image);
    if (index !== -1)
      this.titleImageIndex = index;
  }

  setImageIndex(image: string) {
    const index = this.images.indexOf(image);
    if (index !== -1)
      this.imageIndex = index;
  }

  isFormIncomplete(): boolean {
    switch (this.cipherType) {
      case CipherType.LETTER:
        return (
          this.title === '' ||
          this.categoryName === '' ||
          this.subCategoryName === '' ||
          this.description === '' ||
          this.publicationDates.length === 0 ||
          this.firstnameCipher === '' ||
          this.lastnameCipher === '' ||
          this.streetCipher === '' ||
          this.houseNumberCipher === '' ||
          this.cityCipher === '' ||
          this.zipCipher === '' ||
          (this.localityName === '' && this.subCategoryName === 'Vermietungen') ||
          (!this.localityName && this.subCategoryName === 'Vermietungen') ||
          this.isLoading
        );
      default:
        return (
          this.title === '' ||
          this.categoryName === '' ||
          this.subCategoryName === '' ||
          this.description === '' ||
          this.publicationDates.length === 0 ||
          (this.localityName === '' && this.subCategoryName === 'Vermietungen') ||
          (!this.localityName && this.subCategoryName === 'Vermietungen') ||
          this.isLoading
        );
    }
  }

  formatInput(event: any) {
    // Entfernen alle Nicht-Ziffern aus Eingabe
    const input = event.target.value.replace(/[^0-9]/g, '');

    if (input == '')
      this.price = "0";
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  setCategoryHint() {
    switch (this.categoryName) {
      case 'Immobilien':
        this.categoryHint = 'PROPERTY';
        break;
      case 'Job':
        this.categoryHint = 'JOB';
        break;
      case 'Mobiles':
      case 'Auto':
        this.categoryHint = 'CAR';
        break;
      default:
        this.categoryHint = 'NONE';
        break;
    }
    this.showCategoryHint = true;
  }

  checkPrice(event: any) {
    if (event.target.value === "0")
      this.price = '';
  }

  clearCipher() {
    this.firstnameCipher = '';
    this.lastnameCipher = '';
    this.streetCipher = '';
    this.houseNumberCipher = '';
    this.cityCipher = '';
    this.zipCipher = '';
  }

  async fillCipher() {
    if (this.ownUser !== null && this.ownUser.cipher !== null) {
      console.log(this.ownUser.cipher);
      switch (this.cipherType) {
        case CipherType.LETTER:
          this.firstnameCipher = this.ownUser.cipher.firstname;
          this.lastnameCipher = this.ownUser.cipher.lastname;
          this.streetCipher = this.ownUser.cipher.address.street;
          this.houseNumberCipher = this.ownUser.cipher.address.number;
          this.cityCipher = this.ownUser.cipher.address.city;
          this.zipCipher = this.ownUser.cipher.address.zip;
          break;
        case CipherType.CHAT:
        case CipherType.NONE:
          break;
      }
    }
  }

  areCipherEmpty(): boolean {
    switch (this.cipherType) {
      case CipherType.LETTER:
        return this.firstnameCipher === '' && this.lastnameCipher === '' && this.streetCipher === '' && this.houseNumberCipher === '' && this.cityCipher === '' && this.zipCipher === '';
      case CipherType.CHAT:
      case CipherType.NONE:
        return true;
    }
  }

  private async getOwnUser() {
    try {
      const ownId = this._auth.getSelfID();
      if (ownId !== null) {
        this.ownUser = await this._api.getOwnUser(ownId);
        const data = await this._api.getBillingAddress(ownId);
        if (data) this.billingAddress = data;
      }
    } catch (err) {
      console.log(err);
    }
  }

  getFilteredSubCategoryList() {
    const subCategoryList = this.subCategoryList.filter(subCategory =>
      subCategory.masterCategory === this.categoryName &&
      subCategory.adTypes.includes(this.adType)
    );

    if (subCategoryList.length === 1) this.subCategoryName = subCategoryList[0].name;

    return subCategoryList;
  }

  getCategoryList() {
    const filteredSubCategoryList = this.subCategoryList.filter(subCategory => subCategory.adTypes.includes(this.adType));
    return Array.from(
      new Set(filteredSubCategoryList.map(sub => sub.masterCategory))
    );
  }

  addPublicationDateAmount() {
    if (this.publicationDateAmount <= 8) {
      this.publicationDateAmount += 4;
      this.allAvailableDates = [];
      this.allAvailableDates = this.calculatePublicationDates();
    }
  }

  billingAddressInvalid() {
    return this.billingAddress.firstname === "" ||
      this.billingAddress.lastname === "" ||
      this.billingAddress.street === "" ||
      this.billingAddress.number === "" ||
      this.billingAddress.zip === "" ||
      this.billingAddress.city === "" ||
      (this.isPayableSubCategory() && this.billingAddress.bank === '') ||
      (this.isPayableSubCategory() && this.billingAddress.iban === '') ||
      (this.isPayableSubCategory() && !this.isValidIban());
  }

  private isValidIban() {
    return this.billingAddress.iban && isValidIBANNumber(this.billingAddress.iban);
  }

  private getPreviousAd() {
    const previousAd: Ad | null = getPreviousAd();
    if (previousAd !== null) {
      this.previousAdId = previousAd.id;
      this.titleImageIndex = previousAd.titleImageIndex;
      this.categoryName = previousAd.category.name;
      this.subCategoryName = previousAd.category.subName;
      this.localityName = previousAd.category.localityName;
      this.adType = previousAd.adType;
      this.title = previousAd.title;
      this.description = previousAd.description;
      this.cipherType = previousAd.cipher.type;
      this.priceType = previousAd.priceType;
      this.price = previousAd.price.toString();

      clearPreviousAd();
    }
  }

  changeTransferOldImages(event: any) {
    this.transferOldImages = event.target.checked;
  }

  protected readonly AdType = AdType;
  protected readonly CipherType = CipherType;
  protected readonly PriceType = PriceType;
  protected readonly PRICE_PER_PUBLICATION = PRICE_PER_PUBLICATION;
  protected readonly CIPHER_FEE = CIPHER_FEE;

  onSubCategoryChange() {
    this.localityName = null;
    this.price = '0';
    this.publicationDates = [];
    this.cipherType = CipherType.NONE;
    this.placementFee = 0;
    this.cipherFee = 0;
  }

  isPayableSubCategory(): boolean {
    if (this.subCategoryName === '') return false;

    const subCategory: SubCategory | undefined = this.subCategoryList.find(s => s.name === this.subCategoryName)
    return subCategory?.payable ?? false;
  }

  private async fetchHolidayShifts() {
    try {
      const data = await this._api.getHolidayShifts();
      this.holidayShifts = data.map((d: HolidayShift) => {
        d.appearanceDateOld = new Date(d.appearanceDateOld);
        d.appearanceDateNew = new Date(d.appearanceDateNew);
        d.deadlineOld = new Date(d.deadlineOld);
        d.deadlineNew = new Date(d.deadlineNew);
        return d;
      });
    } catch (e) {
      console.log(e);
    }
  }

  private async fetchCancelledPublications() {
    try {
      const data = await this._api.getCancelledPublications();
      this.cancelledPublications = data.map((d: CancelledPublication) => {
        d.date = new Date(d.date);
        return d;
      });
    } catch (e) {
      console.log(e);
    }
  }
}
